"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    id: "step-1",
    number: "01",
    title: "Hardware Requirements",
    time: "Before you start",
    content: (
      <div className="space-y-4">
        <p className="text-gray-400 text-sm leading-relaxed">
          TapTale Self-Hosted runs comfortably on modest hardware. Here's what you need:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { item: "Raspberry Pi 4 (4GB RAM)", status: "required", note: "Pi 5 recommended for large collections" },
            { item: "32GB+ microSD card", status: "required", note: "Class 10 / A2 rated" },
            { item: "Ethernet connection", status: "recommended", note: "WiFi works but ethernet is faster" },
            { item: "External USB drive", status: "optional", note: "For media storage and backups" },
          ].map((req) => (
            <div key={req.item} className="flex items-start gap-3 p-3 rounded-xl bg-bg-elevated border border-white/5">
              <Icon
                name={req.status === "required" ? "CheckCircleIcon" : req.status === "recommended" ? "StarIcon" : "PlusCircleIcon"}
                size={14}
                className={req.status === "required" ? "text-accent-emerald mt-0.5" : req.status === "recommended" ? "text-accent-gold mt-0.5" : "text-gray-600 mt-0.5"}
              />
              <div>
                <p className="text-white text-xs font-medium">{req.item}</p>
                <p className="text-gray-600 text-[10px]">{req.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "step-2",
    number: "02",
    title: "Flash the OS",
    time: "~10 minutes",
    content: (
      <div className="space-y-4">
        <p className="text-gray-400 text-sm leading-relaxed">
          Flash Raspberry Pi OS Lite (64-bit) to your microSD card using Raspberry Pi Imager. Enable SSH during the flashing process.
        </p>
        <div className="rounded-xl bg-bg-primary border border-white/8 p-4 font-mono text-xs">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
            <span className="w-2 h-2 rounded-full bg-accent-crimson" />
            <span className="w-2 h-2 rounded-full bg-accent-gold" />
            <span className="w-2 h-2 rounded-full bg-accent-emerald" />
            <span className="text-gray-600 ml-2">terminal</span>
          </div>
          <div className="space-y-1.5 text-gray-400">
            <div><span className="text-accent-emerald">$</span> <span className="text-white">ssh pi@taptale.local</span></div>
            <div><span className="text-accent-emerald">$</span> <span className="text-white">sudo apt update && sudo apt upgrade -y</span></div>
            <div><span className="text-accent-emerald">$</span> <span className="text-white">curl -fsSL https://get.docker.com | sh</span></div>
            <div className="text-gray-600"># Docker installed successfully</div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "step-3",
    number: "03",
    title: "Deploy TapTale",
    time: "~5 minutes",
    content: (
      <div className="space-y-4">
        <p className="text-gray-400 text-sm leading-relaxed">
          One command pulls and starts all TapTale services — the API server, media processor, and web interface. Docker Compose handles everything.
        </p>
        <div className="rounded-xl bg-bg-primary border border-white/8 p-4 font-mono text-xs">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
            <span className="w-2 h-2 rounded-full bg-accent-crimson" />
            <span className="w-2 h-2 rounded-full bg-accent-gold" />
            <span className="w-2 h-2 rounded-full bg-accent-emerald" />
            <span className="text-gray-600 ml-2">terminal</span>
          </div>
          <div className="space-y-1.5 text-gray-400">
            <div><span className="text-accent-emerald">$</span> <span className="text-white">curl -fsSL https://get.taptale.app/self-hosted | bash</span></div>
            <div className="text-gray-600"># Pulling taptale/api:latest ...</div>
            <div className="text-gray-600"># Pulling taptale/web:latest ...</div>
            <div className="text-gray-600"># Pulling taptale/media:latest ...</div>
            <div><span className="text-accent-emerald">✓</span> <span className="text-accent-emerald">TapTale running at http://taptale.local:3000</span></div>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 rounded-xl bg-accent-gold/5 border border-accent-gold/20">
          <Icon name="InformationCircleIcon" size={14} className="text-accent-gold mt-0.5 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed">
            The installer automatically configures the database, generates your encryption keys, and sets up the NFC verification service.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "step-4",
    number: "04",
    title: "Configure & Secure",
    time: "~15 minutes",
    content: (
      <div className="space-y-4">
        <p className="text-gray-400 text-sm leading-relaxed">
          Access the setup wizard at <span className="font-mono text-accent-cyan">http://taptale.local:3000/setup</span> to create your admin account, configure storage paths, and set your collection's public URL.
        </p>
        <div className="space-y-3">
          {[
            { step: "Create admin credentials", icon: "UserCircleIcon", color: "text-accent-purple" },
            { step: "Set storage path (USB drive recommended)", icon: "FolderIcon", color: "text-accent-gold" },
            { step: "Configure public URL or Cloudflare Tunnel", icon: "GlobeAltIcon", color: "text-accent-cyan" },
            { step: "Generate your first NFC signing key", icon: "KeyIcon", color: "text-accent-crimson" },
          ].map((item) => (
            <div key={item.step} className="flex items-center gap-3 p-3 rounded-xl bg-bg-elevated border border-white/5">
              <Icon name={item.icon as any} size={14} className={`${item.color} flex-shrink-0`} />
              <span className="text-gray-300 text-xs">{item.step}</span>
              <Icon name="CheckIcon" size={12} className="text-accent-emerald ml-auto" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "step-5",
    number: "05",
    title: "Tag Your First Item",
    time: "~2 minutes",
    content: (
      <div className="space-y-4">
        <p className="text-gray-400 text-sm leading-relaxed">
          Navigate to your TapTale dashboard, create your first item, and use the NFC writer tool to program your tag. The tag is now cryptographically bound to your item.
        </p>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-accent-emerald/5 border border-accent-emerald/20">
          <Icon name="CheckCircleIcon" size={16} className="text-accent-emerald mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-white text-xs font-medium mb-1">You're live!</p>
            <p className="text-gray-400 text-xs leading-relaxed">
              Tap your NFC tag with any phone. It will load your TapTale viewer directly from your Raspberry Pi — fast, private, and entirely under your control.
            </p>
          </div>
        </div>
      </div>
    ),
  },
];

export default function SetupGuide() {
  const [openStep, setOpenStep] = useState<string>("step-1");

  return (
    <section
      id="setup"
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-primary"
      aria-label="Self-hosted setup guide"
    >
      <div className="max-w-container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Header */}
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
              Setup Guide
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight text-white mb-6">
              Up and running
              <br />
              <span className="italic text-gray-400">in 30 minutes.</span>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              No DevOps experience required. If you've ever set up a home router, you can run TapTale Self-Hosted.
            </p>

            {/* Step Navigator */}
            <div className="space-y-2">
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setOpenStep(step.id)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all duration-300 ${
                    openStep === step.id
                      ? "bg-accent-crimson/10 border border-accent-crimson/30" :"border border-transparent hover:border-white/10 hover:bg-bg-card"
                  }`}
                >
                  <span className={`font-mono text-[10px] font-semibold ${openStep === step.id ? "text-accent-crimson" : "text-gray-700"}`}>
                    {step.number}
                  </span>
                  <span className={`text-xs font-medium ${openStep === step.id ? "text-white" : "text-gray-500"}`}>
                    {step.title}
                  </span>
                  <span className={`ml-auto font-mono text-[9px] ${openStep === step.id ? "text-accent-crimson" : "text-gray-700"}`}>
                    {step.time}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Step Content */}
          <div className="lg:col-span-8 space-y-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`rounded-2xl border transition-all duration-500 ${
                  openStep === step.id
                    ? "border-accent-crimson/30 bg-bg-card" :"border-white/5 bg-bg-card/50"
                }`}
              >
                <button
                  onClick={() => setOpenStep(openStep === step.id ? "" : step.id)}
                  className="w-full flex items-center gap-5 p-6 text-left"
                  aria-expanded={openStep === step.id}
                >
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
                    openStep === step.id
                      ? "border-accent-crimson/40 bg-accent-crimson/10" :"border-white/10"
                  }`}>
                    <span className={`font-mono text-xs font-semibold ${openStep === step.id ? "text-accent-crimson" : "text-gray-600"}`}>
                      {step.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-medium text-sm transition-colors ${openStep === step.id ? "text-white" : "text-gray-400"}`}>
                      {step.title}
                    </h3>
                    <p className="font-mono text-[10px] text-gray-600 mt-0.5">{step.time}</p>
                  </div>
                  <Icon
                    name={openStep === step.id ? "ChevronUpIcon" : "ChevronDownIcon"}
                    size={16}
                    className={openStep === step.id ? "text-accent-crimson" : "text-gray-600"}
                  />
                </button>

                <div className={`accordion-content ${openStep === step.id ? "open" : ""}`}>
                  <div className="px-6 pb-6">
                    <div className="h-px bg-white/5 mb-5" />
                    {step.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}